// Shared sales-projection helpers. Used by:
// - Operations · Overview projection chart
// - Sales tab "Projected" weekly card
//
// Approach is intentionally simple-and-explainable rather than ML-driven so the operator
// can predict why a number changed. Two phases stack:
//
//   Phase 1: same-day-of-week average over the last 4 weeks (baseline).
//   Phase 2: trend factor — compares the most-recent 4-week average against the prior 4
//            weeks; if the business is growing, scale the projection up. Capped at +/-50%
//            so a single anomalous week can't blow up tomorrow's number.

function fmtDate(d) { return d.toISOString().split('T')[0]; }

// Compute a per-day projection map for an arbitrary list of target dates.
//   salesByDate:  { 'YYYY-MM-DD': totalSales }  history for the auto computation.
//   overrides:    { 'YYYY-MM-DD': projectedSales } manual operator overrides — these
//                 always win over the auto value, since the operator knows about events
//                 the data can't see (holidays, concerts, weather forecasts).
// Returns { date: projectedSales | null }. The "source" of each value can be inferred:
// if overrides[date] exists it's manual; otherwise auto.
export function projectDailySales(targetDates, salesByDate, overrides = {}) {
  const trendFactor = computeTrendFactor(salesByDate);
  const out = {};
  for (const targetStr of targetDates) {
    if (overrides[targetStr] != null) {
      out[targetStr] = Math.round(Number(overrides[targetStr]));
      continue;
    }
    const target = new Date(targetStr + 'T00:00:00');
    // Pull the same DOW from each of the last 4 weeks. Skip empty weeks so a single great
    // week doesn't get diluted to zero by 3 missing weeks averaged in.
    const samples = [];
    for (let w = 1; w <= 4; w++) {
      const pd = new Date(target); pd.setDate(target.getDate() - 7 * w);
      const v = salesByDate[fmtDate(pd)];
      if (v != null && v > 0) samples.push(Number(v));
    }
    if (samples.length === 0) { out[targetStr] = null; continue; }
    const base = samples.reduce((s, v) => s + v, 0) / samples.length;
    out[targetStr] = Math.round(base * trendFactor);
  }
  return out;
}

// Phase 2 trend factor: ratio of last-4-weeks total to weeks-5-through-8 total.
// Returns 1 (no adjustment) when there isn't enough history yet, or the prior window is empty.
// Clamped to [0.5, 1.5] so one weird week doesn't double a projection.
export function computeTrendFactor(salesByDate) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dayMs = 86400000;
  // Recent window: today-28 to today-1
  let recent = 0, prior = 0;
  for (let i = 1; i <= 28; i++) {
    const d = new Date(today.getTime() - i * dayMs);
    recent += Number(salesByDate[fmtDate(d)] || 0);
  }
  for (let i = 29; i <= 56; i++) {
    const d = new Date(today.getTime() - i * dayMs);
    prior += Number(salesByDate[fmtDate(d)] || 0);
  }
  if (prior <= 0 || recent <= 0) return 1;
  const raw = recent / prior;
  return Math.max(0.5, Math.min(1.5, raw));
}

// Convenience: given a list of dates and salesByDate, return total projected sales across
// those dates (e.g. weekly total). Days with null projection contribute 0.
export function projectTotalSales(targetDates, salesByDate, overrides = {}) {
  const map = projectDailySales(targetDates, salesByDate, overrides);
  return Object.values(map).reduce((s, v) => s + (v || 0), 0);
}
